import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { GraduationCap, MapPinned, ChevronDown, ChevronUp, Building2 } from "lucide-react";

interface UniversitiesProps {
  propertyData: PropertyToEdit | null;
}

const Universities: React.FC<UniversitiesProps> = ({ propertyData }) => {
  const [showUniversities, setShowUniversities] = useState(false);
  const [showNeighborhoods, setShowNeighborhoods] = useState(false);

  const universitiesText = propertyData?.university_nearby || "";
  const neighborhoodsText = propertyData?.about_neighbourhoods || "";

  const shouldTruncateUniversities = universitiesText.length > 200;
  const shouldTruncateNeighborhoods = neighborhoodsText.length > 200;

  const truncatedUniversities = shouldTruncateUniversities
    ? universitiesText.substring(0, 200) + "..."
    : universitiesText;

  const truncatedNeighborhoods = shouldTruncateNeighborhoods
    ? neighborhoodsText.substring(0, 200) + "..."
    : neighborhoodsText;

  const hasUniversities = universitiesText.length > 0;
  const hasNeighborhoods = neighborhoodsText.length > 0;

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
            <Building2 size={24} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Area & Surroundings
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Explore what's nearby
            </p>
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {/* Content Sections */}
      <div className="space-y-8">
        {/* Universities Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <GraduationCap size={18} className="text-[#f5694b] dark:text-orange-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Universities nearby
            </h3>
          </div>

          {hasUniversities ? (
            <div>
              <div className="relative">
                <div
                  className={`prose prose-sm sm:prose-base dark:prose-invert max-w-none transition-all duration-300 ${
                    !showUniversities && shouldTruncateUniversities
                      ? "max-h-24 overflow-hidden"
                      : ""
                  }`}
                >
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {showUniversities ? universitiesText : truncatedUniversities}
                  </p>
                </div>

                {/* Gradient Overlay */}
                {!showUniversities && shouldTruncateUniversities && (
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white dark:from-neutral-900 to-transparent pointer-events-none" />
                )}
              </div>

              {/* Show More/Less Button */}
              {shouldTruncateUniversities && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowUniversities(!showUniversities)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#f5694b] dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-all"
                  >
                    {showUniversities ? (
                      <>
                        Show less
                        <ChevronUp size={14} />
                      </>
                    ) : (
                      <>
                        Show more
                        <ChevronDown size={14} />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 px-6 bg-gradient-to-br from-gray-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-xl border-2 border-dashed border-gray-300 dark:border-neutral-700">
              <div className="flex flex-col items-center text-center space-y-2">
                <GraduationCap size={28} className="text-gray-400 dark:text-gray-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No university information available
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gray-200 dark:bg-neutral-700" />

        {/* Neighborhood Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <MapPinned size={18} className="text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              About the neighborhood
            </h3>
          </div>

          {hasNeighborhoods ? (
            <div>
              <div className="relative">
                <div
                  className={`prose prose-sm sm:prose-base dark:prose-invert max-w-none transition-all duration-300 ${
                    !showNeighborhoods && shouldTruncateNeighborhoods
                      ? "max-h-24 overflow-hidden"
                      : ""
                  }`}
                >
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {showNeighborhoods ? neighborhoodsText : truncatedNeighborhoods}
                  </p>
                </div>

                {/* Gradient Overlay */}
                {!showNeighborhoods && shouldTruncateNeighborhoods && (
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white dark:from-neutral-900 to-transparent pointer-events-none" />
                )}
              </div>

              {/* Show More/Less Button */}
              {shouldTruncateNeighborhoods && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowNeighborhoods(!showNeighborhoods)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-all"
                  >
                    {showNeighborhoods ? (
                      <>
                        Show less
                        <ChevronUp size={14} />
                      </>
                    ) : (
                      <>
                        Show more
                        <ChevronDown size={14} />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 px-6 bg-gradient-to-br from-gray-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-xl border-2 border-dashed border-gray-300 dark:border-neutral-700">
              <div className="flex flex-col items-center text-center space-y-2">
                <MapPinned size={28} className="text-gray-400 dark:text-gray-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No neighborhood information available
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Info Banner */}
      {/* {(hasUniversities || hasNeighborhoods) && (
        <div className="mt-8 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800">
          <p className="text-sm text-indigo-900 dark:text-indigo-100">
            <span className="font-semibold">Want to know more?</span> Contact the owner to get 
            detailed information about transportation, local amenities, and the community vibe.
          </p>
        </div>
      )} */}
    </div>
  );
};

export default Universities;